import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Mail, Bell, Gift, Rocket, Check, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { getStoredRef } from "../lib/referral";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY = "sb-newsletter-popup"; // values: "subscribed" | "dismissed:<ts>"
const SNOOZE_MS = 1000 * 60 * 60 * 24 * 3; // re-ask after 3 days if dismissed

// A/B test: two headlines, assigned 50/50 per visitor. The dashboard shows
// which one converts best (impressions vs conversions).
const AB_TEST = "popup_headline";
const VARIANT_KEY = "sb-popup-variant";
const IMPRESSION_KEY = "sb-popup-impression";
const HEADLINES = {
  A: "Be first to dive in.",
  B: "Get early access + free wallpapers.",
};

export default function NewsletterPopup() {
  const [open, setOpen] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [variant] = useState(() => {
    let v = localStorage.getItem(VARIANT_KEY);
    if (v !== "A" && v !== "B") {
      v = Math.random() < 0.5 ? "A" : "B";
      localStorage.setItem(VARIANT_KEY, v);
    }
    return v;
  });
  const triggered = useRef(false);

  // Count one impression per visitor the first time the popup is shown.
  useEffect(() => {
    if (open && localStorage.getItem(IMPRESSION_KEY) !== "1") {
      localStorage.setItem(IMPRESSION_KEY, "1");
      axios.post(`${API}/ab/impression`, { test: AB_TEST, variant }).catch(() => {});
    }
  }, [open, variant]);

  // Should we ever auto-open? (not subscribed, not recently dismissed)
  const canAutoOpen = () => {
    const v = localStorage.getItem(SEEN_KEY) || "";
    if (v === "subscribed") return false;
    if (v.startsWith("dismissed:")) {
      const ts = parseInt(v.split(":")[1] || "0", 10);
      if (Date.now() - ts < SNOOZE_MS) return false;
    }
    return true;
  };

  useEffect(() => {
    if (localStorage.getItem(SEEN_KEY) === "subscribed") setSubscribed(true);
    if (!canAutoOpen()) return;

    const fire = () => {
      if (triggered.current) return;
      triggered.current = true;
      setOpen(true);
      cleanup();
    };

    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const pct = scrolled / document.body.scrollHeight;
      if (pct > 0.55) fire();
    };
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) fire(); // exit intent (desktop)
    };
    const timer = setTimeout(fire, 30000); // fallback after 30s

    const cleanup = () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseout", onMouseOut);
      clearTimeout(timer);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("mouseout", onMouseOut);
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    setOpen(false);
    if (!subscribed) localStorage.setItem(SEEN_KEY, `dismissed:${Date.now()}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { setError("Please enter a valid email."); return; }
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email: email.trim(), source: "exit-popup", ref: getStoredRef(), abTest: AB_TEST, abVariant: variant }, { timeout: 15000 });
      localStorage.setItem(SEEN_KEY, "subscribed");
      setSubscribed(true);
      trackEvent("sign_up", { method: "exit-popup", ab_variant: variant });
      setEmail("");
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating "Get notified" opener */}
      {!subscribed && !open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="newsletter-float-button"
          className="fixed bottom-5 left-4 md:bottom-24 md:left-6 z-[76] inline-flex items-center gap-2 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm pl-3 pr-4 py-2.5 shadow-[0_10px_30px_rgba(14,127,168,0.4)] hover:-translate-y-0.5 transition-transform"
        >
          <span className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
            <Bell className="w-4 h-4" />
          </span>
          Get game alerts
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="newsletter-popup">
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={dismiss} />
          <div className="relative w-full max-w-md rounded-[2rem] bg-white dark:bg-slate-800 border-4 border-teal-300 dark:border-cyan-500/40 shadow-2xl max-h-[92vh] overflow-y-auto -rotate-1">
            {/* header banner */}
            <div className="relative ocean-gradient px-6 pt-8 pb-10 text-center overflow-hidden">
              {[...Array(4)].map((_, i) => (
                <span key={i} className="absolute bottom-4 rounded-full bg-white/40 animate-bubble"
                  style={{ left: `${18 + i * 22}%`, width: 8 + i * 4, height: 8 + i * 4, animationDelay: `${i * 0.6}s` }} />
              ))}
              <button
                onClick={dismiss}
                data-testid="newsletter-popup-close"
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/25 text-white flex items-center justify-center hover:bg-white/40 transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
              <span className="relative inline-flex items-center gap-2 rounded-full bg-white text-teal-700 px-3 py-1 text-xs font-bold sticker -rotate-2">
                <Bell className="w-3.5 h-3.5" /> Don't miss the splash
              </span>
              <h3 className="relative font-display font-800 text-3xl text-white mt-4 leading-tight">
                {subscribed ? "You're in! 🦈" : HEADLINES[variant]}
              </h3>
            </div>

            <div className="px-6 sm:px-8 py-7 text-center">
              {subscribed ? (
                <p className="text-slate-600 dark:text-slate-300 font-semibold py-2">
                  Thanks for joining the crew! 🦈 Check your inbox to confirm your email — your free Finn wallpaper pack lands right after.
                </p>
              ) : (
                <>
                  <p className="text-slate-500 dark:text-slate-300 font-medium">
                    Join the crew and get:
                  </p>
                  <div className="mt-4 space-y-2 text-left max-w-xs mx-auto">
                    <Perk icon={Rocket} text="Early access to our new games" />
                    <Perk icon={Gift} text="A free Finn wallpaper pack" />
                    <Perk icon={Mail} text="Dev diaries & launch alerts" />
                  </div>
                  <form onSubmit={submit} className="mt-6 space-y-3">
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-teal-500" />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
                        placeholder="you@email.com"
                        data-testid="newsletter-popup-input"
                        className="h-12 pl-12 rounded-full bg-slate-50 dark:bg-slate-900 border border-teal-200 dark:border-white/15 text-slate-800 dark:text-white"
                      />
                    </div>
                    {error && <p data-testid="newsletter-popup-error" className="text-sm font-semibold text-rose-500">{error}</p>}
                    <Button
                      type="submit"
                      disabled={loading}
                      data-testid="newsletter-popup-submit"
                      className="w-full h-12 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold disabled:opacity-70"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Get early access <Rocket className="w-4 h-4 ml-2" /></>}
                    </Button>
                  </form>
                  <button onClick={dismiss} className="mt-3 text-sm font-semibold text-slate-400 hover:text-slate-500">
                    No thanks, maybe later
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Perk({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-8 h-8 rounded-full bg-teal-100 dark:bg-cyan-500/15 text-teal-600 dark:text-cyan-300 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{text}</span>
    </div>
  );
}
